import { createFileRoute, useRouter } from '@tanstack/react-router';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import FormBuilder from 'src/components/form-builder/FormBuilder';
import { format } from 'date-fns';
import debounce from 'lodash.debounce';
import React, { useState } from 'react';
import { Form } from '@repo/shared/domain';
import { getFormBySlug, updateForm } from 'src/api/admin/forms';
import { correctForm } from '@repo/shared/helpers';
import { Alert, CircularProgress } from '@mui/material';

export const Route = createFileRoute('/_admin/config/forms/$slugOrId/build')({
	loader: async ({ params: { slugOrId } }) => {
		const form = await getFormBySlug(slugOrId);
		return { form: form ? correctForm(form) : undefined };
	},
	component: FormBuildPage,
});

// --- SaveStatus ---

const SaveStatus = ({ saving = false, lastSaved }: { saving: boolean; lastSaved: string }) => {
	return (
		<div style={{ flexGrow: 1, textAlign: 'right', marginRight: '1rem' }}>
			<div style={saving ? { color: 'grey' } : { color: '#00CE7C' }}>{saving ? 'saving...' : 'saved'} </div>
			<Typography variant="caption">
				Last saved: <b>{lastSaved}</b>
			</Typography>
		</div>
	);
};

// --- Page Component ---

function FormBuildPage() {
	const { form: originalForm } = Route.useLoaderData();
	const router = useRouter();

	if (!originalForm) {
		return <div className="p-4">Form not found</div>;
	}

	return <FormBuildClient form={originalForm} />;
}

// --- Client Component ---

interface ClientProps {
	form: Form;
}

const FormBuildClient = ({ form: originalForm }: ClientProps) => {
	const [saving, setSaving] = useState(false);
	const [form, setForm] = useState({
		formSchema: { sections: [] },
		...originalForm,
	});
	const [lastSaved, setLastSaved] = useState('Not in this session');
	const router = useRouter();
	const goBack = () => router.history.back();

	const isTranslating = originalForm.translationStatus === 'translating';

	const debouncedSave = React.useRef(
		debounce(async (formToSave: Form & { formSchema: { sections: unknown[] } }) => {
			try {
				await updateForm(formToSave.slug, formToSave);
				setLastSaved(format(Date.now(), 'HH:mm'));
			} catch (_e) {
				//TODO: status update here
			} finally {
				setSaving(false);
			}
		}, 600),
	).current;

	const onChange = (formSchema: { sections: unknown[] }) => {
		setSaving(true);
		const newForm = { ...form, formSchema };
		setForm(newForm);
		debouncedSave(newForm);
	};

	return (
		<div className="h-full flex flex-col gap-4 p-4">
			<div className="flex flex-row items-center justify-between">
				<IconButton sx={{ marginRight: 2 }} onClick={() => goBack()}>
					<ArrowBackIcon />
				</IconButton>
				<Typography variant="h4">Editing {form.title || 'Form'}</Typography>
				<SaveStatus saving={saving} lastSaved={lastSaved} />
			</div>
			{isTranslating ? (
				<Alert severity="warning" icon={<CircularProgress size={20} />}>
					AI translation is currently in progress. The form cannot be edited until translation completes.
				</Alert>
			) : (
				<FormBuilder formSchema={form.formSchema} onChange={onChange} />
			)}
		</div>
	);
};
